import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, RefreshControl, StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { HrAdminApprovalFields } from '../../components/HrAdminApprovalFields';
import { LeaveStatusBadge } from '../../components/LeaveStatusBadge';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ProfileAvatar } from '../../components/ProfileAvatar';
import { RequestCancelActions } from '../../components/RequestCancelActions';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  getWfhModuleCapabilities,
  WFH_ROUTE_CAPABILITIES,
} from '../../config/wfhCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import {
  approveWfhRequest,
  cancelWfhRequest,
  getWfhRequestDetails,
  rejectWfhRequest,
} from '../../services/wfhService';
import { getWorkflowDetails } from '../../services/workflowService';
import type { WfhRequestDetails } from '../../types/wfh';
import type { WorkflowDetails } from '../../types/workflow';
import { isWfhApprovalType } from '../../utils/hrAdminApprovalFields';
import { workflowErrorMessage } from '../../utils/workflowErrors';
import { confirmAction } from '../../utils/confirmAction';
import { wfhErrorMessage } from '../../utils/wfhErrors';
import { canShowApprovedRequestCancel } from '../../utils/requestCancelWindow';
import { isPendingWorkflowActionStatus } from '../../utils/workflowStatus';

type Props = NativeStackScreenProps<RootStackParamList, 'WfhApproval'>;

function mergeWfhRequestDetails(
  workflow: WorkflowDetails,
  wfh: WfhRequestDetails | null | undefined,
): WorkflowDetails {
  if (!wfh) return workflow;
  const owner = wfh.employee;
  const ownerName = owner?.displayName?.trim() || null;
  const ownerNumber = owner?.employeeNumber?.trim() || null;
  const ownerDesignation = owner?.designationName?.trim() || null;
  return {
    ...workflow,
    employee: {
      employeeId: owner?.employeeId ?? workflow.employee.employeeId,
      employeeNumber: ownerNumber || workflow.employee.employeeNumber,
      displayName: ownerName || workflow.employee.displayName,
      departmentName: workflow.employee.departmentName,
      designationName:
        ownerDesignation || workflow.employee.designationName,
    },
    requestDetails: {
      ...(workflow.requestDetails ?? {}),
      fromDate: wfh.fromDate ?? workflow.requestDetails?.fromDate,
      toDate: wfh.toDate ?? workflow.requestDetails?.toDate,
      totalDays: wfh.totalDays ?? workflow.requestDetails?.totalDays,
      requestType: wfh.requestType ?? workflow.requestDetails?.requestType,
      sessionType: wfh.sessionType ?? workflow.requestDetails?.sessionType,
      sessionName: wfh.sessionName ?? workflow.requestDetails?.sessionName,
      durationName: wfh.durationName ?? workflow.requestDetails?.durationName,
      isHalfDay: wfh.durationName ? wfh.isHalfDay : workflow.requestDetails?.isHalfDay,
      reason: wfh.reason ?? workflow.requestDetails?.reason,
      workLocation: wfh.workLocation ?? workflow.requestDetails?.workLocation,
      availabilityNotes:
        wfh.availabilityNotes ?? workflow.requestDetails?.availabilityNotes,
    },
    requestReferenceId: wfh.wfhRequestId ?? workflow.requestReferenceId,
    rejectReason: wfh.rejectReason ?? workflow.rejectReason,
    cancellationReason: wfh.cancellationReason ?? workflow.cancellationReason,
    canCancel: wfh.canCancel,
  };
}

export function WfhApprovalScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const capabilities = getWfhModuleCapabilities(auth.permissions);
  const workflowInstanceId = route.params.workflowInstanceId;

  const [details, setDetails] = useState<WorkflowDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const canAct =
    WFH_ROUTE_CAPABILITIES.approveAction &&
    WFH_ROUTE_CAPABILITIES.rejectAction &&
    isPendingWorkflowActionStatus(details?.workflowStatusCode);
  const wfhRequestId = details?.requestReferenceId ?? null;

  const load = useCallback(
    async (isRefresh = false) => {
      if (!capabilities.canAccessWfhApproval) {
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!Number.isInteger(workflowInstanceId) || workflowInstanceId <= 0) {
        setDetails(null);
        setError('WFH request was not found.');
        setLoading(false);
        setRefreshing(false);
        return;
      }

      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      try {
        const next = await getWorkflowDetails(workflowInstanceId);
        if (next.requestTypeCode && !isWfhApprovalType(next.requestTypeCode)) {
          if (!isRefresh) setDetails(null);
          setError('This approval is not a WFH request.');
          return;
        }

        let resolved = next;
        if (next.requestReferenceId != null) {
          try {
            const wfh = await getWfhRequestDetails(next.requestReferenceId);
            resolved = mergeWfhRequestDetails(next, wfh);
          } catch {
            resolved = next;
          }
        }
        setDetails(resolved);
        setError(null);
      } catch (caught) {
        if (!isRefresh) setDetails(null);
        setError(
          workflowErrorMessage(caught, 'Unable to load WFH approval details.'),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [capabilities.canAccessWfhApproval, workflowInstanceId],
  );

  useEffect(() => {
    load().catch(() => undefined);
  }, [load]);

  const handleApprove = () => {
    if (!canAct || acting || wfhRequestId == null) return;
    confirmAction({
      title: 'Approve WFH',
      message: 'Approve this WFH request?',
      confirmText: 'Approve',
      onConfirm: () => {
        setActing(true);
        setActionError(null);
        return approveWfhRequest(wfhRequestId)
          .then(() => navigation.goBack())
          .catch(caught => {
            setActionError(
              wfhErrorMessage(caught, 'Unable to approve WFH request.'),
            );
          })
          .finally(() => setActing(false));
      },
    });
  };

  const handleReject = async () => {
    if (!canAct || acting || wfhRequestId == null) return;
    const trimmedReason = rejectReason.trim();
    if (!trimmedReason) {
      setActionError('Rejection reason is required.');
      return;
    }
    setActing(true);
    setActionError(null);
    try {
      await rejectWfhRequest(wfhRequestId, trimmedReason);
      navigation.goBack();
    } catch (caught) {
      setActionError(wfhErrorMessage(caught, 'Unable to reject WFH request.'));
    } finally {
      setActing(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (acting || wfhRequestId == null) return;
    setActing(true);
    setActionError(null);
    try {
      await cancelWfhRequest(wfhRequestId, reason);
      navigation.goBack();
    } catch (caught) {
      setActionError(wfhErrorMessage(caught, 'Unable to cancel WFH request.'));
    } finally {
      setActing(false);
    }
  };

  const employeeName =
    details?.employee.displayName ||
    details?.employee.employeeNumber ||
    'Employee unavailable';

  const themed = useMemo(
    () => ({
      name: { ...typography.sectionTitle, color: palette.textPrimary },
      meta: { ...typography.caption, color: palette.textSecondary },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.md,
        textAlign: 'center' as const,
      },
      actionError: {
        ...typography.caption,
        color: palette.error,
        marginBottom: spacing.md,
      },
    }),
    [palette],
  );

  return (
    <PermissionGuard
      permission={PERMISSIONS.WFH_APPROVE}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader title="WFH request detail" onBack={navigation.goBack} />
          <AppLoader message="Loading WFH approval" />
        </AppScreen>
      ) : error && !details ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader title="WFH request detail" onBack={navigation.goBack} />
          <ErrorState message={error} onRetry={load} />
        </AppScreen>
      ) : details ? (
        <AppScreen
          scroll
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                load(true).catch(() => undefined);
              }}
              tintColor={palette.primary}
            />
          }
        >
          <ScreenHeader title={employeeName} onBack={navigation.goBack} />

          <View style={styles.body}>
            <AppCard style={styles.card}>
              <View style={styles.profileRow}>
                <ProfileAvatar name={employeeName} size={56} />
                <View style={styles.profileText}>
                  <Text style={themed.name}>{employeeName}</Text>
                  {details.employee.employeeNumber ? (
                    <Text style={themed.meta}>
                      {details.employee.employeeNumber}
                    </Text>
                  ) : null}
                  {details.employee.designationName ? (
                    <Text style={themed.meta}>
                      {details.employee.designationName}
                    </Text>
                  ) : null}
                </View>
                <LeaveStatusBadge
                  label={details.workflowStatusLabel}
                  statusCode={details.workflowStatusCode}
                />
              </View>

              <HrAdminApprovalFields details={details} palette={palette} />
            </AppCard>

            {actionError ? (
              <Text accessibilityRole="alert" style={themed.actionError}>
                {actionError}
              </Text>
            ) : null}

            {canAct && rejectMode ? (
              <View style={styles.rejectBlock}>
                <AppInput
                  label="Rejection reason"
                  value={rejectReason}
                  onChangeText={value => {
                    setRejectReason(value);
                    setActionError(null);
                  }}
                  placeholder="Enter reason for rejection"
                />
                <AppButton
                  title="Confirm Reject"
                  variant="danger"
                  onPress={handleReject}
                  loading={acting}
                  disabled={acting}
                />
                <AppButton
                  title="Cancel"
                  variant="text"
                  onPress={() => {
                    setRejectMode(false);
                    setRejectReason('');
                    setActionError(null);
                  }}
                  disabled={acting}
                />
              </View>
            ) : canAct ? (
              <View style={styles.actions}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Reject"
                  accessibilityState={{ disabled: !canAct || acting }}
                  disabled={!canAct || acting}
                  onPress={() => {
                    setRejectMode(true);
                    setActionError(null);
                  }}
                  style={[
                    styles.actionButton,
                    styles.rejectButton,
                    {
                      borderColor: palette.error,
                      opacity: canAct && !acting ? 1 : 0.45,
                    },
                  ]}
                >
                  <Text style={[styles.rejectLabel, { color: palette.error }]}>
                    Reject
                  </Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel="Approve"
                  accessibilityState={{ disabled: !canAct || acting }}
                  disabled={!canAct || acting}
                  onPress={handleApprove}
                  style={[
                    styles.actionButton,
                    {
                      backgroundColor: palette.success,
                      borderColor: palette.success,
                      opacity: canAct && !acting ? 1 : 0.45,
                    },
                  ]}
                >
                  <Text style={[styles.approveLabel, { color: palette.onSuccess }]}>
                    Approve
                  </Text>
                </Pressable>
              </View>
            ) : null}

            <RequestCancelActions
              visible={canShowApprovedRequestCancel({
                capabilityCanCancel: capabilities.canCancel,
                canAct,
                statusCode: details.workflowStatusCode,
                fromDate:
                  typeof details.requestDetails?.fromDate === 'string'
                    ? details.requestDetails.fromDate
                    : null,
                toDate:
                  typeof details.requestDetails?.toDate === 'string'
                    ? details.requestDetails.toDate
                    : null,
              })}
              palette={palette}
              startTitle="Cancel WFH"
              confirmTitle="Confirm Cancel"
              keepTitle="Keep WFH"
              busy={acting}
              error={actionError}
              onConfirm={handleCancel}
            />
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center', gap: spacing.md },
  card: { gap: 0, marginBottom: spacing.md, paddingBottom: spacing.sm },
  profileRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.md,
    minWidth: 0,
  },
  profileText: { flex: 1, gap: 2, minWidth: 0 },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  rejectBlock: {
    gap: spacing.md,
    marginBottom: spacing.xl,
  },
  actionButton: {
    flexGrow: 1,
    flexBasis: 140,
    minWidth: 140,
    maxWidth: '100%',
    minHeight: 50,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rejectButton: {
    backgroundColor: 'transparent',
  },
  rejectLabel: {
    ...typography.button,
    fontWeight: '700',
  },
  approveLabel: {
    ...typography.button,
    fontWeight: '700',
  },
});
